import React, { useState } from "react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Enquiry from ${form.name}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name}\n${form.email}`);
    window.location.href = `mailto:contact@rcmnrm.co.in?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Visit the RM.NM Architects studio in Bhavnagar, Gujarat — or write to us at contact@rcmnrm.co.in."
      />

      <section className="container-page pt-16 md:pt-24 pb-12 md:pb-20">
        <Reveal>
          <div className="eyebrow mb-6">Contact</div>
          <p className="max-w-2xl text-base md:text-lg leading-relaxed text-smoke">
            The studio is based in Bhavnagar, Gujarat. For project enquiries, press, or academic
            collaboration — reach out through any of the channels below.
          </p>
        </Reveal>
      </section>

      <section className="container-page pb-24 md:pb-40 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Addresses */}
        <Reveal className="md:col-span-5 space-y-12">
          <div>
            <div className="eyebrow mb-4">Studio — Office</div>
            <address className="not-italic font-display text-xl md:text-2xl tracking-wide leading-relaxed">
              1939/A, Rupani Circle,<br />
              Sardarnagar,<br />
              Bhavnagar — 364001,<br />
              Gujarat, India
            </address>
          </div>

          <div>
            <div className="eyebrow mb-4">Residence</div>
            <address className="not-italic font-display text-xl md:text-2xl tracking-wide leading-relaxed">
              1538 AL 1, Aristoville Society,<br />
              Opposite Champa Tower, Diwdi,<br />
              Rupani Circle,<br />
              Bhavnagar — 364001, Gujarat
            </address>
          </div>

          <div className="space-y-3">
            <div className="eyebrow mb-4">Direct</div>
            <a href="tel:+919426209548" className="block font-display text-2xl tracking-wide hover:opacity-60 transition-opacity">
              +91 94262 09548
            </a>
            <a href="mailto:contact@rcmnrm.co.in" className="block font-display text-2xl tracking-wide hover:opacity-60 transition-opacity">
              contact@rcmnrm.co.in
            </a>
          </div>

          <div className="pt-4">
            <div className="eyebrow mb-4">Follow</div>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/rm.nm.architects/"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-11 h-11 border border-ink/30 rounded-full flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/rm.nm.architects"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-11 h-11 border border-ink/30 rounded-full flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="w-11 h-11 border border-ink/30 rounded-full flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect x="2" y="9" width="4" height="12" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal className="md:col-span-7" delay={0.1}>
          <form onSubmit={onSubmit} className="border border-ink p-8 md:p-12 space-y-8">
            <div>
              <div className="eyebrow mb-2">Enquiry</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-widest2">Write to us</h2>
            </div>

            <div>
              <label htmlFor="name" className="eyebrow block mb-2">Name</label>
              <input
                id="name"
                name="name"
                required
                value={form.name}
                onChange={onChange}
                className="w-full bg-transparent border-b border-ink py-2 font-body text-base focus:outline-none focus:border-b-2"
              />
            </div>

            <div>
              <label htmlFor="email" className="eyebrow block mb-2">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                className="w-full bg-transparent border-b border-ink py-2 font-body text-base focus:outline-none focus:border-b-2"
              />
            </div>

            <div>
              <label htmlFor="message" className="eyebrow block mb-2">Message</label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                value={form.message}
                onChange={onChange}
                className="w-full bg-transparent border-b border-ink py-2 font-body text-base focus:outline-none focus:border-b-2 resize-none"
              />
            </div>

            <button
              type="submit"
              className="group inline-flex items-center gap-3 text-xs tracking-widest2 uppercase border-b border-ink pb-1 hover:opacity-60 transition-opacity"
            >
              <span>Send message</span>
              <span aria-hidden className="group-hover:translate-x-1 transition-transform">→</span>
            </button>

            {sent && (
              <p className="text-xs text-smoke">
                Opening your mail client… if nothing happens, write to contact@rcmnrm.co.in directly.
              </p>
            )}
          </form>
        </Reveal>
      </section>

      {/* MAP */}
      <section className="border-t border-mist">
        <div className="aspect-[16/7] md:aspect-[21/7] w-full bg-mist">
          <iframe
            title="RM.NM Architects — Bhavnagar"
            src="https://www.google.com/maps?q=Rupani+Circle+Sardarnagar+Bhavnagar+Gujarat&output=embed"
            className="w-full h-full border-0 grayscale contrast-110"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>
    </>
  );
}
